import { useMemo, useState, useEffect } from "react";
import { ColumnDef, RowSelectionState } from "@tanstack/react-table";
import { DataTable } from "@/components/ui/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { UserTypeBadge } from "./UserTypeBadge";
import { UnifiedUser } from "@/types/unified-user";
import { SortableHeader, StatusCell, DateTimeCell } from "@/components/ui/table-cells";
import { MonitorSmartphone } from "lucide-react";
import { ActionsDropdown } from "@/components/ui/actions-dropdown";
import { buildUnifiedUserActions } from "@/components/actions";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useNavigate } from "react-router-dom";

interface UnifiedUsersListProps {
  users: UnifiedUser[];
  showTypeColumn?: boolean;
  emptyMessage?: string;
  onSelectionChange?: (users: UnifiedUser[]) => void;
}

export function UnifiedUsersList({ 
  users, 
  showTypeColumn = true,
  emptyMessage = "No users found matching your criteria",
  onSelectionChange
}: UnifiedUsersListProps) {
  const navigate = useNavigate();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  // Calculate selected users
  const selectedUsers = useMemo(() => {
    return Object.keys(rowSelection).length > 0 
      ? users.filter((_, index) => rowSelection[index]) 
      : [];
  }, [rowSelection, users]);

  // Notify parent of selection changes
  useEffect(() => {
    onSelectionChange?.(selectedUsers);
  }, [selectedUsers, onSelectionChange]);

  const columns: ColumnDef<UnifiedUser>[] = useMemo(() => {
    const cols: ColumnDef<UnifiedUser>[] = [
      {
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllPageRowsSelected()}
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
            onClick={(e) => e.stopPropagation()}
          />
        ),
        enableSorting: false,
        enableHiding: false,
        size: 50,
      },
      {
        accessorKey: "name",
        header: ({ column }) => <SortableHeader column={column}>Name</SortableHeader>,
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="font-medium">{row.original.name}</span>
            <span className="text-sm text-muted-foreground">{row.original.email}</span>
          </div>
        ),
        size: 250,
      },
    ];

    if (showTypeColumn) {
      cols.push({
        accessorKey: "type",
        header: "Type",
        cell: ({ row }) => <UserTypeBadge type={row.original.type} />,
        size: 120,
      });
    }

    cols.push(
      {
        accessorKey: "status",
        header: ({ column }) => <SortableHeader column={column}>Status</SortableHeader>,
        cell: ({ row }) => <StatusCell status={row.original.status === 'active'} variant="boolean" />,
        size: 100,
      },
      {
        accessorKey: "department",
        header: ({ column }) => <SortableHeader column={column}>Department</SortableHeader>,
        cell: ({ row }) => row.original.department ? (
          <span className="truncate">{row.original.department}</span>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
        size: 150,
      },
      {
        accessorKey: "title",
        header: ({ column }) => <SortableHeader column={column}>Title</SortableHeader>,
        cell: ({ row }) => row.original.title ? (
          <span className="truncate">{row.original.title}</span>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
        size: 180,
      },
      {
        accessorKey: "roles",
        header: "Roles",
        cell: ({ row }) => {
          if (!row.original.roles || row.original.roles.length === 0) {
            return <span className="text-muted-foreground">-</span>;
          }
          return (
            <div className="flex flex-wrap gap-1">
              {row.original.roles.slice(0, 2).map((userRole) => (
                <Badge key={userRole.id} variant="outline" className="text-xs">
                  {userRole.role?.name}
                </Badge>
              ))}
              {row.original.roles.length > 2 && (
                <Badge variant="secondary" className="text-xs">
                  +{row.original.roles.length - 2}
                </Badge>
              )}
            </div>
          );
        },
        enableSorting: false,
        size: 200,
      },
      {
        accessorKey: "deviceCount",
        header: ({ column }) => <SortableHeader column={column}>Devices</SortableHeader>,
        cell: ({ row }) => {
          if (row.original.deviceCount === 0) {
            return <span className="text-muted-foreground">0</span>;
          }
          
          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 gap-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/devices?user_id=${row.original.directoryId}`);
                    }}
                  >
                    <MonitorSmartphone className="w-4 h-4" />
                    {row.original.deviceCount}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="space-y-1">
                    <p className="font-semibold">Devices:</p>
                    {row.original.devices?.slice(0, 5).map(device => (
                      <p key={device.id} className="text-sm">• {device.name}</p>
                    ))}
                    {row.original.deviceCount > 5 && (
                      <p className="text-sm text-muted-foreground">
                        +{row.original.deviceCount - 5} more
                      </p>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        },
        size: 120,
      },
      {
        accessorKey: "lastActive",
        header: ({ column }) => <SortableHeader column={column}>Last Active</SortableHeader>,
        cell: ({ row }) => <DateTimeCell date={row.original.lastActive} format="relative" />,
        size: 150,
      },
      {
        id: "actions",
        cell: ({ row }) => {
          const user = row.original;
          return (
            <ActionsDropdown
              actions={buildUnifiedUserActions({
                surface: "list",
                hasConsoleAccess: user.type === "console" || user.type === "both",
                onView: () => navigate(`/admin/users-and-roles/${user.id}`),
                onManageRoles: () => {},
                onAssignRole: () => {},
                onEdit: () => {},
                onDelete: () => {},
              })}
            />
          );
        },
        size: 80,
      }
    );

    return cols;
  }, [showTypeColumn, navigate]);

  return (
    <DataTable
      data={users}
      columns={columns}
      enableSorting={true}
      enableColumnFilters={true}
      enableColumnResizing={true}
      enableRowSelection={true}
      rowSelection={rowSelection}
      onRowSelectionChange={setRowSelection}
      emptyMessage={emptyMessage}
      onRowClick={(user) => navigate(`/admin/users-and-roles/${user.id}`)}
    />
  );
}
